if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends HTMLElement {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');

        const debouncedOnChange = debounce((event) => {
          if (parseInt(event.target.dataset.cartQuantity) === 0) {
            this.addToCart(event);
          } else {
            this.updateCart(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
        const pageParams = new URLSearchParams(window.location.search);
        window.pageNumber = decodeURIComponent(pageParams.get('page') || '');
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'quick-add') {
            return;
          }
          // If its another section that made the update
          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      getInput() {
        return this.querySelector('quantity-input input');
      }

      getQuantity() {
        this.quantity = this.querySelector('quantity-input');
        return this.quantity;
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      startLoading() {
        this.getQuantity()?.classList.add('quantity__input-disabled');
        this.selectProgressBar()?.classList.remove('hidden');
      }

      finishLoading() {
        this.getQuantity()?.classList.remove('quantity__input-disabled');
        this.selectProgressBar()?.classList.add('hidden');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.getInput().addEventListener('focusin', (event) => event.target.select());
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.getInput().addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.getInput().blur();
            this.isEnterPressed = true;
          }
        });
      }

      resetQuantityInput(input) {
        if (!input) return;
        input.value = input.getAttribute('value');
        this.isEnterPressed = false;
      }

      stopLoading(event, error) {
        if (error) {
          console.error(error);
        }

        this.finishLoading();

        if (!event || !event.target) return;

        this.resetQuantityInput(event.target);
        event.target.setCustomValidity(window.cartStrings?.error || 'There was an error updating your cart. Please try again.');
        event.target.reportValidity();
        event.target.select();
        this.cleanErrorMessageOnType(event);
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      getQuickAddContainer() {
        return this.closest('.collection-quick-add-bulk') || this.closest('.collection');
      }

      getQuickAddSectionId() {
        const container = this.getQuickAddContainer();
        return container && container.dataset ? container.dataset.id : null;
      }

      getSectionRequestUrl(sectionId) {
        const url = new URL(this.getSectionsUrl(), window.location.origin);
        url.searchParams.set('section_id', sectionId);
        return `${url.pathname}${url.search}`;
      }

      onCartUpdate() {
        return new Promise((resolve, reject) => {
          const sectionId = this.getQuickAddSectionId();
          if (!sectionId) {
            resolve();
            return;
          }

          fetch(this.getSectionRequestUrl(sectionId))
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty =
                html.getElementById(this.id) || html.getElementById(`quick-add-bulk-${this.dataset.id}-${sectionId}`);
              if (sourceQty) {
                this.innerHTML = sourceQty.innerHTML;
                this.getQuantity();
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              reject(e);
            });
        });
      }

      updateCart(event) {
        this.lastActiveInputId = event.target.getAttribute('data-index');
        this.startLoading();
        const body = JSON.stringify({
          quantity: event.target.value,
          id: event.target.getAttribute('data-index'),
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: this.getSectionsUrl(),
        });

        fetch(`${routes.cart_change_url}`, { ...fetchConfig('javascript'), ...{ body } })
          .then((response) => {
            return response.text();
          })
          .then((state) => {
            const parsedState = JSON.parse(state);
            this.finishLoading();
            if (parsedState.description || parsedState.errors) {
              event.target.setCustomValidity(parsedState.description);
              event.target.reportValidity();
              this.resetQuantityInput(event.target);
              this.finishLoading();
              event.target.select();
              this.cleanErrorMessageOnType(event);
              return;
            }

            this.renderSections(parsedState);
            this.finishLoading();

            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });
          })
          .catch((error) => {
            this.stopLoading(event, error);
          });
      }

      addToCart(event) {
        this.startLoading();
        this.lastActiveInputId = event.target.getAttribute('data-index');
        const body = JSON.stringify({
          items: [
            {
              quantity: parseInt(event.target.value),
              id: parseInt(this.dataset.id),
            },
          ],
          sections: this.getSectionsToRender().map((section) => section.section),
        });

        fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), ...{ body } })
          .then((response) => {
            return response.text();
          })
          .then((state) => {
            const parsedState = JSON.parse(state);
            this.finishLoading();
            if (parsedState.description || parsedState.errors) {
              event.target.setCustomValidity(parsedState.description);
              event.target.reportValidity();
              this.resetQuantityInput(event.target);
              this.finishLoading();
              event.target.select();
              this.cleanErrorMessageOnType(event);
              // Error handling
              return;
            }

            this.renderSections(parsedState);
            this.finishLoading();

            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });
          })
          .catch((error) => {
            this.stopLoading(event, error);
          });
      }

      getSectionsToRender() {
        const sectionId = this.getQuickAddSectionId();
        const sections = [
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '#CartDrawer',
            section: 'cart-drawer',
          },
        ];

        if (sectionId) {
          sections.unshift({
            id: this.id || `quick-add-bulk-${this.dataset.id}-${sectionId}`,
            section: sectionId,
            selector: `#quick-add-bulk-${this.dataset.id}-${sectionId}`,
            sourceId: this.id || `quick-add-bulk-${this.dataset.id}-${sectionId}`,
          });
        }

        return sections;
      }

      getSectionsUrl() {
        const url = new URL(window.location.href);
        const searchSourcePage = this.dataset.dtSearchSourcePage;
        if (searchSourcePage) {
          url.searchParams.set('page', searchSourcePage);
        } else if (window.pageNumber) {
          url.searchParams.set('page', window.pageNumber);
        }
        return `${url.pathname}${url.search}`;
      }

      getSectionInnerHTML(html, selector, sourceId) {
        if (!html) return '';
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const element = sourceId ? parsedHTML.getElementById(sourceId) : parsedHTML.querySelector(selector);
        return element ? element.innerHTML : '';
      }

      renderSections(parsedState) {
        this.getSectionsToRender().forEach((section) => {
          if (!parsedState.sections || !parsedState.sections[section.section]) return;

          const sectionElement = document.getElementById(section.id);
          if (
            sectionElement &&
            sectionElement.parentElement &&
            sectionElement.parentElement.classList.contains('drawer')
          ) {
            parsedState.items.length > 0
              ? sectionElement.parentElement.classList.remove('is-empty')
              : sectionElement.parentElement.classList.add('is-empty');

            setTimeout(() => {
              document.querySelector('#CartDrawer-Overlay').addEventListener('click', this.cart.close.bind(this.cart));
            });
          }
          const elementToReplace =
            sectionElement && sectionElement.querySelector(section.selector)
              ? sectionElement.querySelector(section.selector)
              : sectionElement;
          if (elementToReplace) {
            const sectionHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector,
              section.sourceId
            );
            if (sectionHTML) {
              elementToReplace.innerHTML = sectionHTML;
            }
          }
        });

        this.getQuantity();

        if (this.isEnterPressed) {
          this.querySelector(`[data-index="${this.lastActiveInputId}"]`)?.select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }
    }
  );
}
